import React from 'react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';

export function NoticeModal({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Post New Notice</DialogTitle>
          <DialogDescription>Create a notice visible to all community residents</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="notice-title">Title</Label>
            <Input id="notice-title" data-testid="notice-title-input" placeholder="Notice title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notice-content">Content</Label>
            <Textarea id="notice-content" data-testid="notice-content-input" placeholder="Notice content" rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notice-priority">Priority</Label>
            <select data-testid="notice-priority-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notice-expiry">Expiry Date (optional)</Label>
            <Input id="notice-expiry" data-testid="notice-expiry-input" type="date" value={form.expires_at || ''} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} min={new Date().toISOString().split('T')[0]} />
            <p className="text-xs text-slate-400">Notice will automatically hide after this date</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="submit-notice-button" onClick={onSubmit} className="flex-1 bg-gk-teal hover:bg-gk-teal-hover">Post Notice</Button>
            <Button onClick={() => onOpenChange(false)} variant="outline">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function VehicleModal({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Register Vehicle</DialogTitle>
          <DialogDescription>Add a vehicle to the community allowlist</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="vehicle-villa">Villa No *</Label>
            <Input id="vehicle-villa" data-testid="vehicle-villa-input" placeholder="e.g., A-101" value={form.villa_number} onChange={(e) => setForm({ ...form, villa_number: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>User Role</Label>
            <select data-testid="vehicle-user-role-select" value={form.user_role || ''} onChange={(e) => setForm({ ...form, user_role: e.target.value })} className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring">
              <option value="">Select role</option>
              <option value="resident_owner">Resident Owner</option>
              <option value="non_resident_owner">Non-Resident Owner</option>
              <option value="tenant">Tenant</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Vehicle Type *</Label>
            <select data-testid="vehicle-type-select" value={form.vehicle_type || ''} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring">
              <option value="">Select type</option>
              <option value="4-wheeler">4-Wheeler</option>
              <option value="2-wheeler">2-Wheeler</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="vehicle-plate">Plate Number *</Label>
            <Input id="vehicle-plate" data-testid="vehicle-plate-input" placeholder="e.g., KA01AB1234" value={form.plate_number} onChange={(e) => setForm({ ...form, plate_number: e.target.value.toUpperCase() })} className="font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="vehicle-make">Make</Label>
              <Input id="vehicle-make" data-testid="vehicle-make-input" placeholder="e.g., Maruti, Honda" value={form.make || ''} onChange={(e) => setForm({ ...form, make: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vehicle-model">Model</Label>
              <Input id="vehicle-model" data-testid="vehicle-model-input" placeholder="e.g., Swift, City" value={form.vehicle_model || ''} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="vehicle-color">Color</Label>
            <Input id="vehicle-color" data-testid="vehicle-color-input" placeholder="e.g., White, Black, Silver" value={form.color || ''} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="vehicle-ev" data-testid="vehicle-ev-checkbox" checked={form.is_ev || false} onChange={(e) => setForm({ ...form, is_ev: e.target.checked })} className="w-4 h-4 rounded border-slate-300 text-gk-teal focus:ring-gk-teal" />
            <Label htmlFor="vehicle-ev" className="cursor-pointer">Electric Vehicle (EV)</Label>
          </div>
          <div className="flex gap-2">
            <Button data-testid="submit-vehicle-button" onClick={onSubmit} className="flex-1 bg-gk-teal hover:bg-gk-teal-hover">Register Vehicle</Button>
            <Button onClick={() => onOpenChange(false)} variant="outline">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function FacilityModal({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Facility</DialogTitle>
          <DialogDescription>Create a new bookable facility for residents (date-range based)</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="facility-name">Facility Name</Label>
            <Input id="facility-name" data-testid="facility-name-input" placeholder="e.g., Guest House, Club House" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="facility-description">Description</Label>
            <Textarea id="facility-description" data-testid="facility-description-input" placeholder="Facility description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="facility-room-type">Room/Facility Type</Label>
              <Input id="facility-room-type" data-testid="facility-room-type-input" placeholder="e.g., Deluxe, Standard" value={form.room_type || ''} onChange={(e) => setForm({ ...form, room_type: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="facility-capacity">No. of Rooms</Label>
              <Input id="facility-capacity" data-testid="facility-capacity-input" type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 1 })} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="facility-charges">Charges per Night (&#8377;)</Label>
            <Input id="facility-charges" data-testid="facility-charges-input" type="number" placeholder="e.g., 2000" value={form.charges_per_night || ''} onChange={(e) => setForm({ ...form, charges_per_night: parseFloat(e.target.value) || 0 })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="facility-checkin-time">Check-in Time</Label>
              <select id="facility-checkin-time" data-testid="facility-checkin-time-select" value={form.checkin_time || '11:00 AM'} onChange={(e) => setForm({ ...form, checkin_time: e.target.value })} className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring">
                {['06:00 AM','07:00 AM','08:00 AM','09:00 AM','10:00 AM','11:00 AM','12:00 PM','01:00 PM','02:00 PM','03:00 PM','04:00 PM','05:00 PM','06:00 PM'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="facility-checkout-time">Check-out Time</Label>
              <select id="facility-checkout-time" data-testid="facility-checkout-time-select" value={form.checkout_time || '11:00 AM'} onChange={(e) => setForm({ ...form, checkout_time: e.target.value })} className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring">
                {['06:00 AM','07:00 AM','08:00 AM','09:00 AM','10:00 AM','11:00 AM','12:00 PM','01:00 PM','02:00 PM','03:00 PM','04:00 PM','05:00 PM','06:00 PM'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-800">Residents will select check-in and check-out dates when booking. Bookings require management approval.</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="submit-facility-button" onClick={onSubmit} className="flex-1 bg-gk-teal hover:bg-gk-teal-hover">Add Facility</Button>
            <Button onClick={() => onOpenChange(false)} variant="outline">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentModal({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate Invoice</DialogTitle>
          <DialogDescription>Create an invoice for a villa</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="payment-villa">Villa Number</Label>
            <Input id="payment-villa" data-testid="payment-villa-input" placeholder="e.g., A-101" value={form.villa_number} onChange={(e) => setForm({ ...form, villa_number: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="payment-amount">Amount (&#8377;)</Label>
            <Input id="payment-amount" data-testid="payment-amount-input" type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="payment-description">Description</Label>
            <Input id="payment-description" data-testid="payment-description-input" placeholder="e.g., Monthly Maintenance" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="payment-due">Due Date</Label>
            <Input id="payment-due" data-testid="payment-due-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <Button data-testid="submit-payment-button" onClick={onSubmit} className="flex-1 bg-gk-teal hover:bg-gk-teal-hover">Generate Invoice</Button>
            <Button onClick={() => onOpenChange(false)} variant="outline">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SlotManagementModal({ open, onOpenChange, facility, form, setForm, onBlock, onUnblock }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Manage {facility?.name} Availability</DialogTitle>
          <DialogDescription>Block or unblock time slots and dates</DialogDescription>
        </DialogHeader>
        {facility && (
          <div className="space-y-4 py-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800 font-medium mb-1">
                {facility.booking_type === 'date_range' ? 'Block Dates for Offline Bookings' : 'Block Slots for Offline Bookings'}
              </p>
              <p className="text-xs text-amber-700">
                {facility.booking_type === 'date_range'
                  ? 'Block entire dates when all rooms are booked offline'
                  : 'Block time slots that were booked offline to prevent online double-booking'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Start Date</label>
                <input data-testid="slot-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full p-2 border rounded-lg" min={new Date().toISOString().split('T')[0]} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">End Date</label>
                <input data-testid="slot-end-date-input" type="date" value={form.end_date || form.date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full p-2 border rounded-lg" min={form.date || new Date().toISOString().split('T')[0]} />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Reason</label>
              <input data-testid="block-reason-input" type="text" placeholder="e.g., Manual booking by Mr. Sharma" value={form.reason || ''} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full p-2 border rounded-lg text-sm" />
            </div>
            <Button data-testid="block-slot-button" onClick={onBlock} className="w-full bg-red-600 hover:bg-red-700">Block Date(s) for Manual Booking</Button>

            {facility.blocked_slots && facility.blocked_slots.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold mb-3">Currently Blocked {facility.booking_type === 'date_range' ? 'Dates' : 'Slots'}</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {facility.blocked_slots.map((blocked, idx) => (
                    <div key={`${blocked.date}-${blocked.slot || idx}`} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                      <div>
                        <p className="font-medium text-sm">{blocked.date}</p>
                        {blocked.reason && blocked.reason !== 'Manual booking' && <p className="text-xs text-slate-600">{blocked.reason}</p>}
                        {blocked.slot && blocked.slot !== 'FULL_DAY' && blocked.slot !== '' && <p className="text-xs text-slate-600">{blocked.slot}</p>}
                      </div>
                      <Button onClick={() => onUnblock(blocked.date, blocked.slot)} size="sm" variant="outline" className="text-red-600 hover:bg-red-50">Unblock</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {facility.bookings && facility.bookings.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold mb-3">Online Bookings</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {facility.bookings.map((booking) => (
                    <div key={booking.id} className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">
                            {booking.date} → {booking.checkout_date || booking.date}
                          </p>
                          <p className="text-xs text-slate-600">{booking.user_name} (Villa {booking.villa_number})</p>
                          {booking.comments && <p className="text-xs text-blue-600 mt-1 italic">"{booking.comments}"</p>}
                        </div>
                        <span className={`text-xs font-medium ${booking.status === 'approved' ? 'text-emerald-600' : booking.status === 'pending' ? 'text-amber-600' : 'text-red-600'}`}>
                          {booking.status ? booking.status.charAt(0).toUpperCase() + booking.status.slice(1) : 'Confirmed'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={() => onOpenChange(false)} variant="outline" className="w-full">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
